import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import SearchableSelector from "../SearchableSelector";

const BillsPaymentModal = ({ isOpen, setModal }) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const axiosPrivate = useAxiosPrivate();
  const { success, snackError } = useAuth();

  const [walletOptions, setWalletOptions] = useState([]);
  const [serviceProviders, setServiceProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);

  const [bundles, setBundles] = useState([]);
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);

  const [validatingAccount, setValidatingAccount] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setModal((prev) => ({ ...prev, open: false }));
  };

  const extractDataArray = (res) => {
    const body = res?.data || res;
    
    // Check for nested providers key at any depth
    if (body?.data?.data?.providers && Array.isArray(body.data.data.providers)) {
      return body.data.data.providers;
    }
    if (body?.data?.providers && Array.isArray(body.data.providers)) {
      return body.data.providers;
    }
    if (body?.providers && Array.isArray(body.providers)) {
      return body.providers;
    }
    
    // Check for nested arrays inside data.data or data
    if (body?.data?.data && Array.isArray(body.data.data)) {
      return body.data.data;
    }
    if (body?.data && Array.isArray(body.data)) {
      return body.data;
    }
    if (Array.isArray(body)) {
      return body;
    }
    
    // Check if there is an array property inside data.data or data
    if (body?.data?.data && typeof body.data.data === "object") {
      const vals = Object.values(body.data.data);
      const arr = vals.find((v) => Array.isArray(v));
      if (arr) return arr;
    }
    if (body?.data && typeof body.data === "object") {
      const vals = Object.values(body.data);
      const arr = vals.find((v) => Array.isArray(v));
      if (arr) return arr;
    }

    return [];
  };

  const getWallets = async (userObj) => {
    const userId = userObj?.userId || userObj?.id;
    if (!userId) return;
    try {
      const response = await axiosPrivate.get(
        "/auth-serviceV3/api/v1/accounts/user/" + userId + "/details"
      );
      setWalletOptions(response?.data?.data?.accounts || []);
    } catch (error) {
      console.log(error);
      snackError("Failed to fetch user wallets.");
    }
  };

  const handleUserChange = (newValue, setFieldValue) => {
    setFieldValue("username", newValue);
    setFieldValue("accountNumber", "");
    getWallets(newValue);
  };

  const handleBillTypeChange = async (e, setFieldValue) => {
    const billType = e.target.value;
    setFieldValue("billType", billType);
    setFieldValue("serviceType", "");
    setFieldValue("bundle", "");
    setFieldValue("amount", "");
    setFieldValue("phoneNumber", "");
    setFieldValue("smartcardNo", "");
    setFieldValue("meterNo", "");
    setFieldValue("customerName", "");
    setServiceProviders([]);
    setBundles([]);
    setSelectedBundle(null);

    if (!billType) return;

    if (billType === "BETTING") {
      setServiceProviders([
        { name: "BET9JA", service_type: "bet9ja" },
        { name: "NAIRABET", service_type: "nairabet" },
        { name: "SPORTYBET", service_type: "sportybet" },
      ]);
      return;
    }

    setLoadingProviders(true);
    let url = "";

    switch (billType) {
      case "AIRTIME":
        url = "/transaction-payment/api/v1/wayabills/airtime/providers";
        break;
      case "DATA":
        url = "/transaction-payment/api/v1/wayabills/data/providers";
        break;
      case "CABLE":
        url = "/transaction-payment/api/v1/wayabills/cable/providers";
        break;
      case "ELECTRICITY":
        url = "/transaction-payment/api/v1/wayabills/electricity/providers";
        break;
      case "EPIN":
        url = "/transaction-payment/api/v1/wayabills/epin/providers";
        break;
      default:
        setLoadingProviders(false);
        return;
    }

    try {
      const res = await axiosPrivate.get(url);
      if (res?.data?.success || res?.data?.status === true || res?.status === 200) {
        const providers = extractDataArray(res);
        const options = providers
          .map((p) => ({ name: p.name, service_type: p.service_type }))
          .filter((p) => p.service_type);
        // Deduplicate by service_type
        const uniqueOptions = Array.from(
          new Map(options.map((item) => [item.service_type, item])).values()
        );
        setServiceProviders(uniqueOptions);
      } else {
        snackError(`Failed to fetch ${billType} providers.`);
      }
    } catch (error) {
      console.error(error);
      snackError(`Error fetching ${billType} providers.`);
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleServiceTypeChange = async (e, billType, setFieldValue) => {
    const serviceType = e.target.value;
    setFieldValue("serviceType", serviceType);
    setFieldValue("bundle", "");
    setFieldValue("amount", "");
    setBundles([]);
    setSelectedBundle(null);

    if (!billType || !serviceType || !["EPIN", "DATA", "CABLE"].includes(billType)) {
      return;
    }

    setLoadingBundles(true);
    let url = "";

    switch (billType) {
      case "EPIN":
        url = `/transaction-payment/api/v1/wayabills/epin/bundles?serviceType=${serviceType}`;
        break;
      case "DATA":
        url = `/transaction-payment/api/v1/wayabills/data/bundles?serviceType=${serviceType}`;
        break;
      case "CABLE":
        url = `/transaction-payment/api/v1/wayabills/cable/bouquets?serviceType=${serviceType}`;
        break;
      default:
        setLoadingBundles(false);
        return;
    }

    try {
      const res = await axiosPrivate.get(url);
      const dataList = extractDataArray(res);
      let options = [];
      if (billType === "EPIN") {
        options = dataList
          .map((b) => ({ name: b.description || b.name, value: b.amount, ...b }))
          .filter((b) => b.value !== null && b.value !== undefined);
      } else if (billType === "DATA") {
        options = dataList
          .map((b) => ({ name: `${b.name} - ${b.price}`, value: b.datacode || b.code, ...b }))
          .filter((b) => b.value !== null && b.value !== undefined);
      } else if (billType === "CABLE") {
        options = dataList
          .map((b) => ({ name: b.name, value: b.code, ...b }))
          .filter((b) => b.value !== null && b.value !== undefined);
      }
      setBundles(options);
    } catch (error) {
      console.error(error);
      snackError(`Error fetching bundles for ${serviceType}.`);
    } finally {
      setLoadingBundles(false);
    }
  };

  const handleValidate = async (values, setFieldValue) => {
    const { billType, serviceType, smartcardNo, meterNo, phoneNumber } = values;
    if (!serviceType) {
      snackError("Please select a service type first.");
      return;
    }

    let account_number = "";
    switch (billType) {
      case "CABLE":
        account_number = smartcardNo;
        break;
      case "ELECTRICITY":
        account_number = meterNo;
        break;
      case "BETTING":
        account_number = phoneNumber;
        break;
      default:
        return;
    }

    if (!account_number) {
      snackError("Please enter the number to validate.");
      return;
    }

    setValidatingAccount(true);
    try {
      const payload = { service_type: serviceType, account_number: account_number };
      const res = await axiosPrivate.post(
        "/transaction-payment/api/v1/wayabills/validate",
        payload
      );
      const resData = res?.data || res;
      if (resData?.success || resData?.status === true) {
        const name = resData?.data?.user?.name || resData?.data?.name;
        if (name) {
          setFieldValue("customerName", name);
          success("Account validated successfully!");
        } else {
          snackError("Validation successful, but no customer name was returned.");
        }
      } else {
        snackError(resData?.message || "Failed to validate account.");
      }
    } catch (error) {
      console.error(error);
      snackError(error?.response?.data?.message || "An error occurred during validation.");
    } finally {
      setValidatingAccount(false);
    }
  };

  const handleFormSubmit = async (values, { resetForm }) => {
    if (["AIRTIME", "DATA", "EPIN", "BETTING"].includes(values.billType) && !values.phoneNumber) {
      snackError("Phone number is required");
      return;
    }
    if (values.billType === "CABLE" && !values.smartcardNo) {
      snackError("Smartcard number is required");
      return;
    }
    if (values.billType === "ELECTRICITY" && !values.meterNo) {
      snackError("Meter number is required");
      return;
    }

    setIsLoading(true);

    let billPayloadObject = {};
    if (["AIRTIME", "BETTING"].includes(values.billType)) {
      billPayloadObject.phone = values.phoneNumber;
    } else if (values.billType === "DATA") {
      billPayloadObject.phone = values.phoneNumber;
      if (selectedBundle) {
        billPayloadObject.datacode = selectedBundle.datacode || selectedBundle.code || values.bundle;
        billPayloadObject.bundleName = selectedBundle.name;
        billPayloadObject.bundlePrice = selectedBundle.price || selectedBundle.amount || values.amount;
      }
    } else if (values.billType === "EPIN") {
      billPayloadObject.phone = values.phoneNumber;
      if (selectedBundle) {
        billPayloadObject.epinAmount = selectedBundle.amount || selectedBundle.price || values.amount;
        billPayloadObject.epinDescription = selectedBundle.description || selectedBundle.name;
      }
    } else if (values.billType === "CABLE") {
      billPayloadObject.smartcardNo = values.smartcardNo;
      if (selectedBundle) {
        billPayloadObject.bouquetCode = selectedBundle.code || selectedBundle.datacode || values.bundle;
        billPayloadObject.bouquetName = selectedBundle.name;
      }
    } else if (values.billType === "ELECTRICITY") {
      billPayloadObject.meter = values.meterNo;
    }

    const payload = {
      userId: String(values.username?.userId || values.username?.id || ""),
      billType: values.billType,
      amount: Number(values.amount),
      accountNumber: values.accountNumber,
      serviceType: values.serviceType,
      billPayload: JSON.stringify(billPayloadObject),
      customerName: values.customerName || `${values.username?.firstName || ""} ${values.username?.surname || ""}`.trim(),
      details: values.details || `${values.billType} payment via support dashboard`,
      makerReason: values.makerReason,
      expiryHours: Number(values.expiryHours) || 24,
    };

    try {
      const res = await axiosPrivate.post(
        "/transaction-payment/api/v1/admin/maker-checker/maker/bills-payment",
        payload
      );
      const resData = res?.data || res;
      if (resData?.success || resData?.status === true) {
        success(resData?.message || "Bills payment request created successfully.");
        resetForm();
        handleClose();
      } else {
        snackError(resData?.message || "Failed to create bills payment request.");
      }
    } catch (error) {
      console.error(error);
      snackError(error?.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  const initialValues = {
    username: null,
    billType: isOpen?.billType || "",
    serviceType: "",
    bundle: "",
    amount: "",
    accountNumber: "",
    phoneNumber: "",
    smartcardNo: "",
    meterNo: "",
    customerName: "",
    details: "",
    makerReason: "",
    expiryHours: 24,
  };

  const validationSchema = yup.object().shape({
    username: yup.object().nullable().required("Required"),
    billType: yup.string().required("Required"),
    serviceType: yup.string().required("Required"),
    amount: yup.number().required("Required").positive("Must be greater than 0"),
    accountNumber: yup.string().required("Required"),
    makerReason: yup.string().required("Required"),
    expiryHours: yup.number().required("Required").integer().positive(),
    details: yup.string().nullable(),
    customerName: yup.string().required("Required"),
  });

  return (
    <Modal open={isOpen.open} onClose={handleClose}>
      <Box
        sx={{
          width: "60%",
          maxHeight: "85vh",
          overflowY: "auto",
          backgroundColor: colors.primary[400],
          p: 4,
          position: "relative",
          top: "50%",
          left: "55%",
          transform: "translate(-50%, -50%)",
          borderRadius: "8px",
        }}
      >
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box padding={2}>
                <Typography
                  variant="h3"
                  sx={{ textAlign: "center", marginBottom: "30px", fontWeight: "bold" }}
                >
                  Create Pending Bills Payment (Maker)
                </Typography>
                <Box
                  display="grid"
                  gap="20px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": {
                      gridColumn: isNonMobile ? undefined : "span 4",
                    },
                    marginBottom: "10px",
                  }}
                >
                  {/* Select User */}
                  <SearchableSelector
                    value={values.username}
                    name="username"
                    sx={{ gridColumn: "span 4" }}
                    handleChange={(e, newValue) =>
                      handleUserChange(newValue, setFieldValue)
                    }
                  />

                  {/* Wallet Account Number */}
                  <TextField
                    fullWidth
                    select
                    label="Source Wallet Account"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.accountNumber}
                    name="accountNumber"
                    error={!!touched.accountNumber && !!errors.accountNumber}
                    helperText={touched.accountNumber && errors.accountNumber}
                    sx={{ gridColumn: "span 2" }}
                  >
                    {walletOptions?.map((option, index) => {
                      const acctName = option.accountName || option.acct_name || "";
                      const accNo = option.accountNumber || option.accountNo || "";
                      return (
                        <MenuItem key={index} value={accNo}>
                          {acctName + " - " + accNo}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {/* Bill Type */}
                  <TextField
                    label="Bill Type"
                    fullWidth
                    select
                    onBlur={handleBlur}
                    onChange={(e) => handleBillTypeChange(e, setFieldValue)}
                    value={values.billType}
                    name="billType"
                    error={!!touched.billType && !!errors.billType}
                    helperText={touched.billType && errors.billType}
                    sx={{ gridColumn: "span 2" }}
                  >
                    <MenuItem value="AIRTIME">AIRTIME</MenuItem>
                    <MenuItem value="DATA">DATA</MenuItem>
                    <MenuItem value="CABLE">CABLE / CABLE_TV</MenuItem>
                    <MenuItem value="ELECTRICITY">ELECTRICITY</MenuItem>
                    <MenuItem value="EPIN">E-PIN (EPIN)</MenuItem>
                    <MenuItem value="BETTING">BETTING</MenuItem>
                  </TextField>

                  {/* Service Type / Provider */}
                  {values.billType && (
                    <TextField
                      label="Service Provider"
                      fullWidth
                      select
                      disabled={loadingProviders}
                      onBlur={handleBlur}
                      onChange={(e) =>
                        handleServiceTypeChange(e, values.billType, setFieldValue)
                      }
                      value={values.serviceType}
                      name="serviceType"
                      error={!!touched.serviceType && !!errors.serviceType}
                      helperText={touched.serviceType && errors.serviceType}
                      sx={{ gridColumn: "span 2" }}
                    >
                      {serviceProviders?.map((option, index) => (
                        <MenuItem key={index} value={option.service_type}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  {/* Bundle / Bouquet */}
                  {values.billType &&
                    values.serviceType &&
                    ["EPIN", "DATA", "CABLE"].includes(values.billType) && (
                      <TextField
                        label={values.billType === "CABLE" ? "Bouquet" : "Bundle"}
                        fullWidth
                        select
                        disabled={loadingBundles}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          const bundleVal = e.target.value;
                          setFieldValue("bundle", bundleVal);
                          const bundleObj = bundles.find((b) => b.value === bundleVal);
                          setSelectedBundle(bundleObj);
                          if (bundleObj) {
                            if (bundleObj.price) {
                              setFieldValue("amount", bundleObj.price);
                            } else if (bundleObj.amount) {
                              setFieldValue("amount", bundleObj.amount);
                            }
                          }
                        }}
                        value={values.bundle}
                        name="bundle"
                        error={!!touched.bundle && !!errors.bundle}
                        helperText={touched.bundle && errors.bundle}
                        sx={{ gridColumn: "span 2" }}
                      >
                        {bundles?.map((option, index) => (
                          <MenuItem key={index} value={option.value}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}

                  {/* Phone Number Field */}
                  {values.billType &&
                    ["AIRTIME", "DATA", "EPIN", "BETTING"].includes(values.billType) && (
                      <Box sx={{ gridColumn: "span 2", display: "flex", gap: "10px", alignItems: "center" }}>
                        <TextField
                          fullWidth
                          variant="filled"
                          type="text"
                          label="Recipient Phone Number"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.phoneNumber}
                          name="phoneNumber"
                          error={!!touched.phoneNumber && !!errors.phoneNumber}
                          helperText={touched.phoneNumber && errors.phoneNumber}
                        />
                        {values.billType === "BETTING" && (
                          <Button
                            variant="contained"
                            color="secondary"
                            disabled={validatingAccount}
                            onClick={() => handleValidate(values, setFieldValue)}
                            sx={{ height: "55px", px: 3 }}
                          >
                            Verify
                          </Button>
                        )}
                      </Box>
                    )}

                  {/* Smartcard Number Field */}
                  {values.billType === "CABLE" && (
                    <Box sx={{ gridColumn: "span 2", display: "flex", gap: "10px", alignItems: "center" }}>
                      <TextField
                        fullWidth
                        variant="filled"
                        type="text"
                        label="Smartcard Number"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.smartcardNo}
                        name="smartcardNo"
                        error={!!touched.smartcardNo && !!errors.smartcardNo}
                        helperText={touched.smartcardNo && errors.smartcardNo}
                      />
                      <Button
                        variant="contained"
                        color="secondary"
                        disabled={validatingAccount}
                        onClick={() => handleValidate(values, setFieldValue)}
                        sx={{ height: "55px", px: 3 }}
                      >
                        Verify
                      </Button>
                    </Box>
                  )}

                  {/* Meter Number Field */}
                  {values.billType === "ELECTRICITY" && (
                    <Box sx={{ gridColumn: "span 2", display: "flex", gap: "10px", alignItems: "center" }}>
                      <TextField
                        fullWidth
                        variant="filled"
                        type="text"
                        label="Meter Number"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.meterNo}
                        name="meterNo"
                        error={!!touched.meterNo && !!errors.meterNo}
                        helperText={touched.meterNo && errors.meterNo}
                      />
                      <Button
                        variant="contained"
                        color="secondary"
                        disabled={validatingAccount}
                        onClick={() => handleValidate(values, setFieldValue)}
                        sx={{ height: "55px", px: 3 }}
                      >
                        Verify
                      </Button>
                    </Box>
                  )}

                  {/* Customer Name Field */}
                  {values.billType && (
                    <TextField
                      fullWidth
                      variant="filled"
                      type="text"
                      label="Customer Name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.customerName}
                      name="customerName"
                      error={!!touched.customerName && !!errors.customerName}
                      helperText={touched.customerName && errors.customerName}
                      sx={{ gridColumn: "span 2" }}
                    />
                  )}

                  {/* Amount Field */}
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Amount"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.amount}
                    name="amount"
                    error={!!touched.amount && !!errors.amount}
                    helperText={touched.amount && errors.amount}
                    sx={{ gridColumn: "span 2" }}
                  />

                  {/* Expiry Hours Field */}
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Request Expiry Hours"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.expiryHours}
                    name="expiryHours"
                    error={!!touched.expiryHours && !!errors.expiryHours}
                    helperText={touched.expiryHours && errors.expiryHours}
                    sx={{ gridColumn: "span 2" }}
                  />

                  {/* Details Field */}
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Details/Narration"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.details}
                    name="details"
                    error={!!touched.details && !!errors.details}
                    helperText={touched.details && errors.details}
                    sx={{ gridColumn: "span 2" }}
                  />

                  {/* Maker Reason Field */}
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Maker Reason (e.g. Call Center Request)"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.makerReason}
                    name="makerReason"
                    error={!!touched.makerReason && !!errors.makerReason}
                    helperText={touched.makerReason && errors.makerReason}
                    sx={{ gridColumn: "span 4" }}
                  />
                </Box>
                <Button
                  type="submit"
                  color="secondary"
                  disabled={isLoading || validatingAccount}
                  variant="contained"
                  sx={{ width: "100%", p: 2, mt: 3, fontWeight: "bold", fontSize: "16px" }}
                >
                  Create Pending Bills Payment
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default BillsPaymentModal;
